import { useEffect, useRef } from 'react';

/** Minimum horizontal swipe distance (px) to trigger back navigation. */
const SWIPE_BACK_THRESHOLD = 50;

/**
 * useSwipeBack — fires a callback when the user swipes a touch element
 * in the given direction by more than SWIPE_BACK_THRESHOLD pixels.
 *
 * @param {React.RefObject<HTMLElement>} ref - element to attach listeners to
 * @param {Function} onSwipeBack - callback fired when a valid swipe is detected
 * @param {'left'|'right'} direction - which swipe direction triggers the callback
 * @param {boolean} enabled - only attaches listeners when true
 */
const useSwipeBack = (ref, onSwipeBack, direction, enabled) => {
	const startXRef = useRef(null);

	useEffect(() => {
		if (!enabled) return;

		const el = ref.current;
		if (!el) return;

		const handleTouchStart = (e) => {
			startXRef.current = e.touches[0].clientX;
		};

		const handleTouchMove = (e) => {
			if (startXRef.current === null) return;

			const deltaX = e.touches[0].clientX - startXRef.current;

			if (Math.abs(deltaX) > SWIPE_BACK_THRESHOLD) {
				const swipedLeft = deltaX < 0;
				const swipedRight = deltaX > 0;

				if (
					(direction === 'left' && swipedLeft) ||
					(direction === 'right' && swipedRight)
				) {
					startXRef.current = null;
					onSwipeBack();
				}
			}
		};

		el.addEventListener('touchstart', handleTouchStart, { passive: true });
		el.addEventListener('touchmove', handleTouchMove, { passive: true });

		// eslint-disable-next-line consistent-return
		return () => {
			el.removeEventListener('touchstart', handleTouchStart);
			el.removeEventListener('touchmove', handleTouchMove);
		};
	}, [ref, onSwipeBack, direction, enabled]);
};

export default useSwipeBack;
